import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

@Injectable()
export class DashboardInterceptor implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  constructor(private authService: AuthService, private localStorageService: LocalStorageService, private toastr: ToastrService, private router: Router) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    //for request without authorization and Reset Password bypass
    if (request.headers.get('No-Auth') == "True" || request.url.indexOf('appsettings.production.json') > -1)
      return next.handle(request.clone());

    if (this.localStorageService.getItem('accesstoken') == null || this.localStorageService.getItem('currentUser') == null) {
      this.authService.logout();
      this.router.navigateByUrl('login');
      return next.handle(request.clone());
    }

    var clonedreq = request.clone({
      headers: request.headers.set("Authorization", "Bearer " + this.localStorageService.getItem('accesstoken'))
        .set("path", (this.router.url.split('/')[this.router.url.split('/').length - 1]).split('?')[0])
    });

    return next.handle(clonedreq).pipe(
      catchError((err) => {
        if (err.status === 401 && !request.url.includes('/login')) {
          return this.handle401Error(request, next);
        }
        else {
          if (request.headers.get('No-Error-Message') != "True") {
            switch (err.status) {
              case 0: {
                this.router.navigate(['login']);
                this.authService.logout();
                this.toastr.error('API call failed.', 'Error Message');
                break;
              }
              case 400: {
                if (String(err.error).toLowerCase().includes('refresh token expired' || 'invalid client request')) {
                  this.authService.logout();
                  break;
                }
                this.toastr.error(err.error, 'Error Message');
                break;
              }
              case 403: {
                this.toastr.error('Unauthorized!', 'Error Message');
                this.router.navigate(['login']);
                this.authService.logout();
                break;
              }
              case 404: {
                this.router.navigate(['login']);
                break;
              }
              case 503: {
                this.toastr.error(err.statusText, 'Error Message');
                break;
              }
              default: {
                this.toastr.error(err.error, 'Error Message');
                break;
              }
            };
          }
          return throwError(err);
        }
      }));
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log("--should refresh token--");
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);
      return this.authService
        .refreshToken()
        .pipe(
          switchMap((token: any) => {
            this.isRefreshingToken = false;
            this.tokenSubject.next(token.accesstoken);
            return next.handle(this.addToken(request, token.accesstoken));
          }),
          catchError((error) => {
              // If there is an exception calling 'refreshToken', bad news so logout.
              this.authService.logout();
              this.router.navigate(['login']);
              return throwError(error); 
          })
        );
    } else {
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(jwt => {
          return next.handle(this.addToken(request, jwt));
        })
      );
    }
  }

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      headers: req.headers.set("Authorization", "Bearer " + this.localStorageService.getItem('accesstoken'))
        .set("path", (this.router.url.split('/')[this.router.url.split('/').length - 1]).split('?')[0])
    });
  }
}
